import type {
  CoachDigitalTwinRuntime,
  CoachFrameworkReference,
  CoachIdentityProfile,
  CoachPhilosophyProfile,
  CoachRuntimeContextType,
  CoachVoiceProfile,
} from "@/lib/behavior/behaviorContracts";
import {
  buildCoachIdentityProfile,
  buildCoachIdentityProfileFromSources,
} from "@/lib/behavior/coachIdentityRuntime";
import {
  extractPersistedFrameworkPackSnapshot,
  resolvePersistedVoiceCustomizationStatus,
} from "@/lib/behavior/coachIdentityPersistence";
import type { CoachLensSource, CoachTwinSource } from "@/lib/coachSessionReviewContract";

function trimText(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function dedupe(values: Array<string | null | undefined>, limit = 8): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const normalized = trimText(value);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(normalized);
    if (result.length >= limit) break;
  }
  return result;
}

export function buildCoachPhilosophyProfile(
  coachLens?: CoachLensSource | null,
): CoachPhilosophyProfile {
  const frameworkPack = extractPersistedFrameworkPackSnapshot(coachLens);
  const frameworks: CoachFrameworkReference[] = (coachLens?.frameworks || [])
    .map((item) => ({
      name: trimText(item?.name || "") || "",
      summary: trimText(item?.summary),
    }))
    .filter((item) => item.name.length > 0);
  if (frameworks.length === 0 && frameworkPack) {
    frameworks.push({
      name: frameworkPack.label,
      summary: trimText(frameworkPack.preview.philosophySummary),
    });
  }
  const philosophy = trimText(coachLens?.coach_lens_json?.philosophy)
    || trimText(frameworkPack?.preview.philosophySummary);
  const preferredLanguage = (coachLens?.preferred_language || []).filter(
    (value): value is string => typeof value === "string" && value.trim().length > 0,
  );
  const avoidLanguage = (coachLens?.avoid_language || []).filter(
    (value): value is string => typeof value === "string" && value.trim().length > 0,
  );

  let knowledgeCoverage: CoachPhilosophyProfile["lens"]["knowledgeCoverage"] = "none";
  if (frameworkPack) {
    knowledgeCoverage = "full";
  } else if (frameworks.length > 0) {
    const withSummaryCount = frameworks.filter((item) => item.summary).length;
    knowledgeCoverage = withSummaryCount === frameworks.length ? "full" : "partial";
  }

  return {
    present: Boolean(philosophy || frameworks.length || trimText(coachLens?.lens_name)),
    lens: {
      source: coachLens ? "declared_coach_lens" : "executive_coaching_default",
      lensName: trimText(coachLens?.lens_name),
      philosophy,
      frameworks,
      frameworkPackId: frameworkPack?.id || null,
      frameworkPackLabel: frameworkPack?.label || null,
      frameworkPack,
      preferredLanguage,
      avoidLanguage,
      knowledgeCoverage,
    },
    intendedStandards: dedupe([philosophy, ...frameworks.map((item) => item.summary)], 5),
  };
}

export function buildCoachVoiceProfile(
  coachTwin?: CoachTwinSource | null,
): CoachVoiceProfile {
  const questionStems = (coachTwin?.question_stems || []).filter(
    (value): value is string => typeof value === "string" && value.trim().length > 0,
  );
  const phraseBank = (coachTwin?.phrase_bank || []).filter(
    (value): value is string => typeof value === "string" && value.trim().length > 0,
  );
  const avoidList = (coachTwin?.avoid_list || []).filter(
    (value): value is string => typeof value === "string" && value.trim().length > 0,
  );
  const guardrails = (coachTwin?.guardrails || []).filter(
    (value): value is string => typeof value === "string" && value.trim().length > 0,
  );
  const voiceSummary = trimText(coachTwin?.voice_summary);
  const customizationStatus = voiceSummary
    || questionStems.length
    || phraseBank.length
    || avoidList.length
    || guardrails.length
    ? "customized"
    : resolvePersistedVoiceCustomizationStatus(coachTwin);

  return {
    present: customizationStatus !== "default",
    source: customizationStatus === "customized"
      ? "coach_twin_profile"
      : customizationStatus === "framework_default"
        ? "framework_pack_default"
        : "default",
    customizationStatus,
    voiceSummary,
    questionStems,
    phraseBank,
    avoidList,
    guardrails,
  };
}

export function buildCoachDigitalTwinRuntime(params: {
  philosophyProfile?: CoachPhilosophyProfile;
  voiceProfile?: CoachVoiceProfile;
  identityProfile?: CoachIdentityProfile;
  contextType: CoachRuntimeContextType;
}): CoachDigitalTwinRuntime {
  const philosophyProfile = params.identityProfile?.philosophy || params.philosophyProfile!;
  const voiceProfile = params.identityProfile?.voice || params.voiceProfile!;
  const identityProfile = params.identityProfile || buildCoachIdentityProfile({
    philosophyProfile,
    voiceProfile,
  });

  return {
    status: philosophyProfile.present || voiceProfile.present ? "configured" : "defaulted",
    contextType: params.contextType,
    identity: identityProfile,
    philosophy: philosophyProfile,
    voice: voiceProfile,
    expressionPolicy: {
      preferredLanguage: philosophyProfile.lens.preferredLanguage,
      avoidLanguage: dedupe([
        ...philosophyProfile.lens.avoidLanguage,
        ...voiceProfile.avoidList,
      ]),
      guardrails: voiceProfile.guardrails,
    },
  };
}

export function buildCoachDigitalTwinRuntimeFromSources(params: {
  coachLens?: CoachLensSource | null;
  coachTwin?: CoachTwinSource | null;
  contextType: CoachRuntimeContextType;
}): CoachDigitalTwinRuntime {
  const philosophyProfile = buildCoachPhilosophyProfile(params.coachLens);
  const voiceProfile = buildCoachVoiceProfile(params.coachTwin);
  const identityProfile = buildCoachIdentityProfileFromSources({
    philosophyProfile,
    voiceProfile,
    coachLens: params.coachLens,
    coachTwin: params.coachTwin,
  });

  return buildCoachDigitalTwinRuntime({
    contextType: params.contextType,
    identityProfile,
  });
}
